import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Get, Post } from "../../../utils/apiService";
import {
  GetRolePrivilegesBody,
  AddRolePrivilegesBody,
  AddUpdateUserBody,
  ValidateUserNameBody,
  ValidateUserEmailBody,
  DeleteUserBody,
  AddUpdateDetailCodeBody,
  DeleteDetailCodeParams,
  AddUpdateVisitPurposeBody,
  DeleteVisitPurposeParams,
  IsVisitPurposeExistsBody,
  DecodedUser,
  VerifyOTPBody,
  ResendOTPBody,
  ExportUsersForExcelPDFBody,
  AddUpdateProfileBody,
} from "../interface";
import useAuthStore from "../../../store/Auth";
import { FETCH_USER_DETAILS_KEYS } from "../query";
import { FETCH_COMMON_DETAILS_KEYS } from "../../common/query";
import { jwtDecode } from "jwt-decode";
import { loginApiUrl } from "../../../constants/common";

const revokeTokenAction = async () => {
  return Post({
    url: "/api/Auth/RevokeToken",
    isCredentialInclude: true,
  });
};

export const useRevokeToken = () => {
  return useMutation({
    mutationFn: async () => revokeTokenAction(),
  });
};

const addUpdateProfileAction = async (body: AddUpdateProfileBody) => {
  return Post({
    url: "/api/EditProfile/AddUpdateProfile",
    body,
  });
};

export const useAddUpdateProfile = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateProfileBody) => addUpdateProfileAction(body),
  });
};

const logoutSSOUserAction = async () => {
  return Get({
    url: "/GetSSOLogoutUri",
    params: {
      provider: process.env.REACT_APP_SSO_PROVIDER,
    },
  });
};

export const useLogoutSSOUser = () => {
  return useMutation({
    mutationFn: async () => logoutSSOUserAction(),
  });
};

const getSSORedirectionUrlAction = async () => {
  return Get({
    url: "/GetSSOUrl",
    params: {
      provider: process.env.REACT_APP_SSO_PROVIDER,
    },
  });
};

export const useGetSSORedirectionUrl = () => {
  return useMutation({
    mutationFn: async () => getSSORedirectionUrlAction(),
  });
};

const getCaptchaImageAction = async (data: { url: string }) => {
  return Get({
    url: data.url,
    noBaseUrl: true,
    isReturnBlob: true,
  });
};

export const useGetCaptchaImage = () => {
  return useMutation({
    mutationFn: async (data: { url: string }) => getCaptchaImageAction(data),
  });
};

const verifyOTPAction = async (body: VerifyOTPBody) => {
  return Post({
    url: "/api/Login/ValidateOTP",
    body,
  });
};

export const useVerifyOTP = () => {
  const { setUserData, setIsLoggedIn, setToken } = useAuthStore();

  return useMutation({
    mutationFn: async (body: VerifyOTPBody) => verifyOTPAction(body),
    onSuccess: (data) => {
      if (data.token) {
        setIsLoggedIn(true);

        setToken(data.token);

        const decodedUser: DecodedUser = jwtDecode(data.token);

        const user = {
          user_id: Number(decodedUser?.UserId),
          username: decodedUser?.Username,
          firstname: decodedUser?.FullName.split(" ")[0],
          lastname: decodedUser?.FullName.split(" ")[1],
          email: decodedUser?.Email,
          n_RoleID: Number(decodedUser?.RoleId),
          n_LocationID: Number(decodedUser?.LocationID),
        };
        setUserData(user);
      }
    },
  });
};

const resendOTPAction = async (body: ResendOTPBody) => {
  return Post({
    url: "/api/Login/ResendOTP",
    body,
  });
};

export const useResendOTP = () => {
  return useMutation({
    mutationFn: async (body: ResendOTPBody) => resendOTPAction(body),
  });
};

const exportUsersForExcelAction = async (body: ExportUsersForExcelPDFBody) => {
  return Post({
    url: "/api/Export/Excel/SearchUsers",
    body,
    isReturnBlob: true,
  });
};

export const useExportUsersForExcel = () => {
  return useMutation({
    mutationFn: async (body: ExportUsersForExcelPDFBody) =>
      exportUsersForExcelAction(body),
  });
};

const exportUsersForPDFAction = async (body: ExportUsersForExcelPDFBody) => {
  return Post({
    url: "/api/Export/PDF/SearchUsers",
    body,
    isReturnBlob: true,
  });
};

export const useExportUsersForPDF = () => {
  return useMutation({
    mutationFn: async (body: ExportUsersForExcelPDFBody) =>
      exportUsersForPDFAction(body),
  });
};

const captchaLoginUserAction = async (body: FormData) => {
  return Post({
    url: loginApiUrl,
    body,
    isFormData: true,
    isFormUrlEncoded: true,
    isCredentialInclude: true,
  });
};

export const useCaptchaLoginUser = () => {
  const { setUserData, setIsLoggedIn, setToken } = useAuthStore();

  return useMutation({
    mutationFn: async (body: FormData) => captchaLoginUserAction(body),
    onSuccess: (data) => {
      if (data.token) {
        setIsLoggedIn(true);

        setToken(data.token);

        const decodedUser: DecodedUser = jwtDecode(data.token);

        const user = {
          user_id: Number(decodedUser?.UserId),
          username: decodedUser?.Username,
          firstname: decodedUser?.FullName.split(" ")[0],
          lastname: decodedUser?.FullName.split(" ")[1],
          email: decodedUser?.Email,
          n_RoleID: Number(decodedUser?.RoleId),
          n_LocationID: Number(decodedUser?.LocationID),
        };
        setUserData(user);
      }
    },
  });
};

const getRolePrivilegesAction = async (body: GetRolePrivilegesBody) => {
  return Post({
    url: "/api/Roles/GetRolePrivileges",
    body,
  });
};

export const useGetRolePrivileges = () => {
  return useMutation({
    mutationFn: async (body: GetRolePrivilegesBody) =>
      getRolePrivilegesAction(body),
  });
};

const getUserPrivilegesAction = async () => {
  return Post({
    url: "/api/Users/GetUserPrivileges",
  });
};

export const useGetUserPrivileges = () => {
  return useMutation({
    mutationFn: async () => getUserPrivilegesAction(),
  });
};

const addRolePrivilegesAction = async (body: AddRolePrivilegesBody) => {
  return Post({
    url: "/api/Roles/AddRolePrivileges",
    body,
  });
};

export const useAddRolePrivileges = () => {
  return useMutation({
    mutationFn: async (body: AddRolePrivilegesBody) =>
      addRolePrivilegesAction(body),
  });
};

const addUserAction = async (body: AddUpdateUserBody) => {
  return Post({
    url: "/api/Users/Add",
    body,
  });
};

export const useAddUser = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateUserBody) => addUserAction(body),
  });
};

const validateUserNameAction = async (body: ValidateUserNameBody) => {
  return Post({
    url: "/api/Users/ValidateUserName",
    body,
  });
};

export const useValidateUserName = () => {
  return useMutation({
    mutationFn: async (body: ValidateUserNameBody) =>
      validateUserNameAction(body),
  });
};

const validateUserEmailAction = async (body: ValidateUserEmailBody) => {
  return Post({
    url: "/api/Users/ValidateEmail",
    body,
  });
};

export const useValidateUserEmail = () => {
  return useMutation({
    mutationFn: async (body: ValidateUserEmailBody) =>
      validateUserEmailAction(body),
  });
};

const deleteUserAction = async (body: DeleteUserBody) => {
  return Post({
    url: "/api/Users/Delete",
    body,
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: DeleteUserBody) => deleteUserAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_USER_DETAILS_KEYS.FETCH_ALL_USERS],
      });
    },
  });
};

const updateUserAction = async (body: AddUpdateUserBody) => {
  return Post({
    url: "/api/Users/Update",
    body,
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateUserBody) => updateUserAction(body),
  });
};

const addUpdateDetailCodeAction = async (body: AddUpdateDetailCodeBody) => {
  return Post({
    url: "/api/SystemCodes/AddUpdateDetailCode",
    body,
  });
};

export const useAddUpdateDetailCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateDetailCodeBody) =>
      addUpdateDetailCodeAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_SYSTEM_DETAIL_CODES],
      });
    },
  });
};

const deleteDetailCodeAction = async (params: DeleteDetailCodeParams) => {
  return Post({
    url: "/api/SystemCodes/DeleteDetailCode",
    params,
  });
};

export const useDeleteDetailCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteDetailCodeParams) =>
      deleteDetailCodeAction(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_SYSTEM_DETAIL_CODES],
      });
    },
  });
};

const addUpdateVisitPurposeAction = async (body: AddUpdateVisitPurposeBody) => {
  return Post({
    url: "/api/VisitPurposes/AddUpdate",
    body,
  });
};

export const useAddUpdateVisitPurpose = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddUpdateVisitPurposeBody) =>
      addUpdateVisitPurposeAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_VISIT_PURPOSES],
      });
    },
  });
};

const isVisitPurposeExistsAction = async (body: IsVisitPurposeExistsBody) => {
  return Post({
    url: "/api/VisitPurposes/IsExists",
    body,
  });
};

export const useIsVisitPurposeExists = () => {
  return useMutation({
    mutationFn: async (body: IsVisitPurposeExistsBody) =>
      isVisitPurposeExistsAction(body),
  });
};

const deleteVisitPurposeAction = async (params: DeleteVisitPurposeParams) => {
  return Post({
    url: "/api/VisitPurposes/Delete",
    params,
  });
};

export const useDeleteVisitPurpose = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteVisitPurposeParams) =>
      deleteVisitPurposeAction(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_COMMON_DETAILS_KEYS.FETCH_ALL_VISIT_PURPOSES],
      });
    },
  });
};
